import { Logger } from "../helpers";
import DataManager from "./config/DataManager";
import ServerTrackerManager from "./serverTrack/ServerTrackerManager";
import ClientTrackerManager from "./clientTrack/ClientTrackerManager";
import { EVENTS } from "./config/CONSTANTS";
import { CookieBanner } from "./Cookie";
import { generateUniqueId } from "../helpers/securityUtils";
import { resolvePlatforms } from "./config/trackingStrategy"; // Importing centralized platform resolver
import { checkGeoAndSetConsentCookie } from "./GeoConsentManager";

// TODO - It looks like the user email is not getting passed around?

// Freeze event types for consistency across modules
const EventTypes = Object.freeze({ ...EVENTS });

/**
 * @fileoverview Entry point for managing analytics and event tracking within the application.
 * @module Analytics
 */

/**
 * @typedef {keyof typeof Analytics.EventTypes | string} EventName
 * @description A string representing one of the predefined event names or a custom event name.
 */

/**
 * @typedef {Object} EventData
 * @property {string} [formId]
 * @property {string} [formAction]
 * @property {string} [email_hash]
 * @property {Object} [customData]
 */

/**
 * The `Analytics` class provides a centralized way to handle event tracking across server-side and client-side platforms.
 * It initializes tracking managers, handles consent gating, and exposes a unified tracking interface.
 */
class Analytics {
  constructor() {
    if (Analytics._instance) {
      throw new Error("Use Analytics.getInstance()");
    }

    // Init the ready state
    this.ready = false;

    // Initialize tracking and data managers
    this.dataManager = new DataManager();
    this.clientTrackerManager = new ClientTrackerManager();
    this.serverTrackerManager = new ServerTrackerManager();
    this.cookieBanner = null;

    // Init an event queue
    this.eventQueue = [];
    this.isFlushingQueue = false;

    // Setup flexible tracking API
    this.setupTrackFunction();

    // Expose Analytics globally for other modules to reference
    window.analytics = this;
    window.EventTypes = EventTypes;
  }

  /**
   * Singleton getter
   * Ensures that only one instance of Analytics is created and initialized.
   */
  static getInstance() {
    if (!Analytics._instance) {
      try {
        Analytics._instance = new Analytics();
        Logger.log("Analytics object created successfully.");
        Analytics._instance.initialize();
      } catch (error) {
        Logger.error("Error creating Analytics object:", error);
      }
    }
    return Analytics._instance;
  }

  /**
   * Initializes tracking managers and cookie banner logic
   * Called automatically when instance is created
   */
  async initialize() {
    try {
      Logger.log("Running geo check before tracking...");

      // Check if we are in Cali
      await checkGeoAndSetConsentCookie();

      // Instantiate CookieBanner inside initialize()
      this.cookieBanner = new CookieBanner();
      this.cookieBanner.initialize();

      // Initialize data managers
      await Promise.all([
        this.dataManager.initialize( this.cookieBanner.hasOptedOut() ),
        this.clientTrackerManager.initialize(),
        this.serverTrackerManager.initialize()
      ]);

      // Dispatch ready event for modules waiting on Analytics load
      window.dispatchEvent(new Event("analyticsReady"));
      this.ready = true;

      // Flush event queue
      await this.flushEventQueue();

      Logger.log("Analytics initialized successfully.");
    } catch (error) {
      Logger.error("Error initializing Analytics:", error);
    }
  }

  async flushEventQueue() {
    if (this.isFlushingQueue || this.eventQueue.length === 0) return;
    this.isFlushingQueue = true;

    Logger.log(`Flushing ${this.eventQueue.length} queued events...`);

    while (this.eventQueue.length > 0) {
      const { eventName, customData, options } = this.eventQueue.shift();
      await this.trackEvent(eventName, customData, options);
    }

    this.isFlushingQueue = false;
  }


  /**
   * Creates the `track` method with scoped server/client helpers
   */

  // TODO - This has some weirdness, not 100% sure what it is yet, but it seems convoluted to control what platform is used for tracking.

  setupTrackFunction() {
    const trackFunction = (eventName, customData, options) =>
      this.trackEvent(eventName, customData, options);

    trackFunction.server = (eventName, customData, platforms = "all") =>
      this.trackEvent(eventName, customData, { client: false, server: platforms });

    trackFunction.client = (eventName, customData, platforms = "all") =>
      this.trackEvent(eventName, customData, { client: platforms, server: false });

    // Expose as grouped `track` object
    this.track = Object.assign(trackFunction, {
      all: trackFunction,
      server: trackFunction.server,
      client: trackFunction.client,
    });
  }

  /**
   * Tracks an event to server/client platforms with opt-out check
   * Uses centralized platform resolution strategy.
   *
   * @param {EventName} eventName - The event name to track.
   * @param {EventData} customData - Custom payload associated with the event.
   * @param {Object} options - Configuration to specify server/client targeting.
   */
  async trackEvent(eventName, customData = {}, options = { server: "all", client: "all" }) {
    // Check if the object is ready to track events
    if ( !this.ready ) {
      Logger.warn("Analytics not ready, queuing event:", eventName);
      this.eventQueue.push({ eventName, customData, options });
      return;
    }

    try {
      const eventId = generateUniqueId();
      const finalEventName = EventTypes[eventName] || eventName;

      const eventData = await this.dataManager.prepareCommonPayload(
        finalEventName,
        customData,
        eventId
      );

      // Respect opt-out preference from cookieBanner
      if (this.cookieBanner?.hasOptedOut()) {
        Logger.log(`Event not tracked due to user opt-out: ${finalEventName}`, eventData);
        return;
      }

      const promises = [];

      // Send to client if allowed
      if ( options.client ) {
        promises.push(this.clientTrackerManager.trackEvent(finalEventName, eventData, options.client));
      }

      // Send to server if allowed
      if ( options.server ) {
        promises.push(this.serverTrackerManager.trackEvent(finalEventName, eventData, options.server));
      }

      await Promise.all(promises);
      Logger.log(`Event tracked successfully: ${finalEventName}`, eventData);
    } catch (error) {
      Logger.error(`Failed to track event: ${eventName}`, error);
    }
  }

  // Expose event types
  static get EventTypes() {
    return EventTypes;
  }
}

export default Analytics;
export { EventTypes };
